import React, { useState, useEffect } from 'react';
import styles from './Record.module.css';
import withEditRights from '../hoc/with-edit-rights';
import apiClient from '../../util/api-client';

// Hjälpfunktion för att hantera IndexedDB (behålls som lokal fallback och för automatisk migrering)
const initDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('WinnersDatabase', 1);

        request.onerror = (event) => {
            console.error('IndexedDB-fel:', event.target.error);
            reject('Kunde inte öppna databasen');
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('winners')) {
                db.createObjectStore('winners', { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains('images')) {
                db.createObjectStore('images', { keyPath: 'id' });
            }
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };
    });
};

const getAllFromIndexedDB = (storeName) => {
    return new Promise(async (resolve, reject) => {
        try {
            const db = await initDB();
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => {
                console.error(`Fel vid hämtning från ${storeName}:`, event.target.error);
                reject(event.target.error);
            };
        } catch (error) {
            console.error(`Kunde inte hämta från ${storeName}:`, error);
            resolve([]);
        }
    });
};

const getFromIndexedDB = (storeName, id) => {
    return new Promise(async (resolve, reject) => {
        try {
            const db = await initDB();
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const request = store.get(id);

            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => {
                reject(event.target.error);
            };
        } catch (error) {
            resolve(null);
        }
    });
};

const Record = ({ canEdit }) => {
    const [winners, setWinners] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [winnerToRemove, setWinnerToRemove] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editModal, setEditModal] = useState({ show: false, index: null, name: '', description: '' });

    // Hjälpfunktion för att komprimera bilder med rimlig komprimering (~20-30 KB)
    const compressImage = (base64String, maxWidth = 320, quality = 0.7) => {
        return new Promise((resolve, reject) => {
            try {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = Math.floor(height * (maxWidth / width));
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedBase64);
                };

                img.onerror = () => {
                    reject(new Error('Kunde inte ladda bilden för komprimering'));
                };

                img.src = base64String;
            } catch (error) {
                reject(error);
            }
        });
    };

    // Ladda sparade vinnare från MongoDB Atlas (med automatisk migrering av lokala IndexedDB-kort)
    useEffect(() => {
        let isMounted = true;

        const loadWinners = async () => {
            setIsLoading(true);
            try {
                // 1. Hämta live-vinnare från MongoDB Atlas
                let cloudWinners = await apiClient.getRecordWinners();

                // 2. Kontrollera om det finns sparade kort i lokal IndexedDB (t.ex. "Khalid" som skapades lokalt)
                try {
                    const localWinners = await getAllFromIndexedDB('winners');
                    if (localWinners && localWinners.length > 0) {
                        const localWithImages = await Promise.all(
                            localWinners.map(async (winner) => {
                                try {
                                    const imageData = await getFromIndexedDB('images', winner.id);
                                    return {
                                        ...winner,
                                        image: imageData ? imageData.data : (winner.image || "")
                                    };
                                } catch (e) {
                                    return winner;
                                }
                            })
                        );

                        // Filtrera ut lokala kort som har namn eller bild
                        const namedLocal = localWithImages.filter(w => (w.name && w.name.trim() !== '') || w.image);
                        if (namedLocal.length > 0) {
                            let merged = Array.isArray(cloudWinners) ? [...cloudWinners] : [];
                            let hasNew = false;

                            for (const localW of namedLocal) {
                                const exists = merged.some(cw => cw.name === localW.name || cw.id === localW.id);
                                if (!exists) {
                                    merged.push(localW);
                                    hasNew = true;
                                }
                            }

                            if (hasNew) {
                                await apiClient.saveRecordWinners(merged);
                                cloudWinners = merged;
                            }
                        }
                    }
                } catch (localErr) {
                    console.warn("Lokal synkroniseringsnotis:", localErr.message);
                }

                if (cloudWinners && cloudWinners.length > 0) {
                    if (isMounted) setWinners(cloudWinners);
                } else {
                    // Skapa 5 initiala kort om databasen är helt tom
                    const initialWinners = Array(5).fill().map((_, index) => ({
                        id: `winner-${index}-${Date.now()}`,
                        name: "",
                        description: "Utmärkt prestation som visar enastående engagemang och kreativitet. En förebild för andra elever.",
                        image: ""
                    }));

                    await apiClient.saveRecordWinners(initialWinners);
                    if (isMounted) setWinners(initialWinners);
                }
            } catch (error) {
                console.error("Fel vid laddning av vinnare från databasen:", error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadWinners();

        return () => {
            isMounted = false;
        };
    }, []);

    // Funktion för att öppna redigeringsmodal
    const openEditModal = (index = null) => {
        if (!canEdit) return;

        if (index !== null && winners[index]) {
            setEditModal({
                show: true,
                index,
                name: winners[index].name || '',
                description: winners[index].description || 'Utmärkt prestation som visar enastående engagemang och kreativitet. En förebild för andra elever.'
            });
        } else {
            setEditModal({
                show: true,
                index: null,
                name: '',
                description: 'Utmärkt prestation som visar enastående engagemang och kreativitet. En förebild för andra elever.'
            });
        }
    };

    // Funktion för att stänga modal
    const closeEditModal = () => {
        setEditModal({ show: false, index: null, name: '', description: '' });
    };

    // Funktion för att spara ändringar i MongoDB Atlas
    const saveChanges = async () => {
        if (!canEdit) return;

        try {
            let updatedWinners = [];
            if (editModal.index !== null) {
                // Uppdatera befintligt kort
                const updatedWinner = {
                    ...winners[editModal.index],
                    name: editModal.name,
                    description: editModal.description
                };
                updatedWinners = [...winners];
                updatedWinners[editModal.index] = updatedWinner;
            } else {
                // Lägg till nytt kort
                const newWinner = {
                    id: `winner-${winners.length}-${Date.now()}`,
                    name: editModal.name,
                    description: editModal.description,
                    image: "",
                    isLoading: false
                };
                updatedWinners = [...winners, newWinner];
            }

            setWinners(updatedWinners);
            await apiClient.saveRecordWinners(updatedWinners);
            closeEditModal();
        } catch (error) {
            console.error("Fel vid sparande av ändringar:", error);
            alert("Det gick inte att spara ändringarna i databasen. Försök igen.");
        }
    };

    // Lägg till en ny vinnare
    const addWinner = async () => {
        if (!canEdit) return;

        try {
            const newWinner = {
                name: "",
                description: "Utmärkt prestation som visar enastående engagemang och kreativitet. En förebild för andra elever.",
                image: "",
                id: `winner-${winners.length}-${Date.now()}`
            };

            const newWinners = [...winners, newWinner];
            setWinners(newWinners);
            await apiClient.saveRecordWinners(newWinners);
        } catch (error) {
            console.error("Fel vid tillägg av vinnare:", error);
            alert("Det gick inte att lägga till en ny vinnare. Försök igen.");
        }
    };

    // Förbered borttagning
    const prepareRemoveWinner = (indexToRemove) => {
        if (!canEdit) return;
        setWinnerToRemove(indexToRemove);
        setShowConfirmation(true);
    };

    // Ta bort en vinnare från MongoDB Atlas
    const removeWinner = async () => {
        if (winnerToRemove !== null) {
            try {
                const newWinners = winners.filter((_, index) => index !== winnerToRemove);
                setWinners(newWinners);
                setShowConfirmation(false);
                setWinnerToRemove(null);
                await apiClient.saveRecordWinners(newWinners);
            } catch (error) {
                console.error("Fel vid borttagning av vinnare:", error);
                alert("Det gick inte att ta bort vinnaren. Försök igen.");
            }
        }
    };

    const cancelRemove = () => {
        setShowConfirmation(false);
        setWinnerToRemove(null);
    };

    // Hantera bildändringar med komprimering och synk till MongoDB
    const handleImageChange = async (index, event) => {
        if (!canEdit) return;
        const file = event.target.files[0];
        if (file) {
            try {
                const newWinners = [...winners];
                newWinners[index].isLoading = true;
                setWinners(newWinners);

                const reader = new FileReader();

                reader.onloadend = async () => {
                    try {
                        const compressedImage = await compressImage(reader.result, 320, 0.7);
                        const updatedWinner = {
                            ...winners[index],
                            image: compressedImage,
                            isLoading: false
                        };

                        const updatedWinners = [...winners];
                        updatedWinners[index] = updatedWinner;
                        setWinners(updatedWinners);

                        // Spara till MongoDB Atlas
                        await apiClient.saveRecordWinners(updatedWinners);
                    } catch (error) {
                        console.error("Fel vid bildhantering:", error);
                        const updatedWinners = [...winners];
                        updatedWinners[index].isLoading = false;
                        setWinners(updatedWinners);
                        alert("Det gick inte att spara bilden. Försök igen.");
                    }
                };

                reader.onerror = () => {
                    console.error("Fel vid läsning av fil");
                    const updatedWinners = [...winners];
                    updatedWinners[index].isLoading = false;
                    setWinners(updatedWinners);
                    alert("Det gick inte att läsa bilden. Försök igen.");
                };

                reader.readAsDataURL(file);
            } catch (error) {
                console.error("Fel vid hantering av fil:", error);
                alert("Ett fel uppstod. Försök igen.");
            }
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.winnersGrid}>
                {winners.map((winner, index) => (
                    <div
                        key={winner.id || `winner-${index}`}
                        className={styles.flipCard}
                        onClick={() => openEditModal(index)}
                        style={{ cursor: canEdit ? 'pointer' : 'default' }}
                    >
                        {/* Image Layer */}
                        <div className={styles.cardImageContainer}>
                            {winner.isLoading ? (
                                <div className={styles.loadingIndicator}>Laddar...</div>
                            ) : winner.image ? (
                                <img
                                    src={winner.image}
                                    alt={winner.name || "Vinnare"}
                                    className={styles.cardImage}
                                />
                            ) : (
                                <div className={styles.cardImage} style={{
                                    background: `linear-gradient(135deg, 
                                        hsl(${(winner.name || 'default').length * 137 % 360}, 70%, 60%) 0%, 
                                        hsl(${(winner.name || 'default').length * 217 % 360}, 70%, 40%) 100%)`
                                }} />
                            )}
                        </div>

                        {/* Name (Always Visible) */}
                        <h3 className={styles.cardTitle}>{winner.name || 'Ny Vinnare'}</h3>

                        {/* Description Overlay (Visible on Hover) */}
                        <div className={styles.cardContent}>
                            <p className={styles.cardDescription}>
                                {winner.description || 'Utmärkt prestation som visar enastående engagemang och kreativitet. En förebild för andra elever.'}
                            </p>

                            {/* Action Buttons - ONLY VISIBLE IF CANEDIT */}
                            {canEdit && (
                                <div className={styles.actionButtons}>
                                    <button
                                        className={styles.editButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditModal(index);
                                        }}
                                    >
                                        ✏️ Redigera
                                    </button>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            prepareRemoveWinner(index);
                                        }}
                                    >
                                        🗑️ Ta bort
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Knapp för att lägga till en ny vinnare - ONLY VISIBLE IF CANEDIT */}
            {canEdit && (
                <button
                    onClick={addWinner}
                    className={styles.addButton}
                    title="Lägg till vinnare"
                    disabled={isLoading}
                >
                    +
                </button>
            )}

            {/* Bekräftelsedialog för borttagning */}
            {showConfirmation && (
                <div className={styles.confirmationDialog}>
                    <div className={styles.confirmationContent}>
                        <h3>Ta bort vinnare</h3>
                        <p>Är du säker på att du vill ta bort denna vinnare?</p>
                        <div className={styles.confirmationButtons}>
                            <button
                                onClick={removeWinner}
                                className={styles.confirmButton}
                            >
                                Ja, ta bort
                            </button>
                            <button
                                onClick={cancelRemove}
                                className={styles.cancelButton}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Redigeringsmodal */}
            {editModal.show && (
                <div className={styles.editModal}>
                    <div className={styles.editModalContent}>
                        <h2 className={styles.editModalTitle}>
                            {editModal.index !== null ? 'Redigera Vinnare' : 'Lägg till Ny Vinnare'}
                        </h2>

                        <div className={styles.editForm}>
                            <input
                                type="text"
                                className={styles.editInput}
                                placeholder="Ange namn"
                                value={editModal.name}
                                onChange={(e) => setEditModal({ ...editModal, name: e.target.value })}
                            />

                            <textarea
                                className={styles.editTextarea}
                                placeholder="Ange beskrivning av prestationen"
                                value={editModal.description}
                                onChange={(e) => setEditModal({ ...editModal, description: e.target.value })}
                            />

                            {editModal.index !== null && (
                                <label className={styles.imageInputLabel}>
                                    📷 Välj ny bild (valfritt)
                                    <input
                                        type="file"
                                        className={styles.imageInput}
                                        onChange={(e) => {
                                            if (editModal.index !== null) {
                                                handleImageChange(editModal.index, e);
                                            }
                                        }}
                                        accept="image/*"
                                    />
                                </label>
                            )}
                        </div>

                        <div className={styles.editButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={closeEditModal}
                            >
                                Avbryt
                            </button>
                            <button
                                className={styles.saveButton}
                                onClick={saveChanges}
                                disabled={!editModal.name.trim()}
                            >
                                {editModal.index !== null ? 'Spara ändringar' : 'Lägg till'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Laddningsindikator för hela sidan */}
            {isLoading && (
                <div className={styles.pageLoadingIndicator}>
                    Laddar vinnare...
                </div>
            )}
        </div>
    );
};

export default withEditRights(Record);
